import { Text, View } from '@react-pdf/renderer';
import { RHeader, RPage, RViewer } from '../../../../components/PdfRender';
import { RSignature } from '../../../../components/PdfRender/RSignature';
import {
  ILinkedTreatmentProduct,
  IRetailTreatmentItem,
  ITreatment,
  ITreatmentItem,
} from '../../../interfaces';
import * as S from './styles';
import { useCompany } from '../../../../hooks/company';
import { currency } from '../../../utils/functions';
import { formatDate } from '../../../utils/format-date';
import { useAuth } from '../../../../hooks/auth';

interface ITreatmentReportProps {
  treatment: ITreatment | null;
  items: ITreatmentItem[];
  linkedProducts?: ILinkedTreatmentProduct[];
  retailItems?: IRetailTreatmentItem[];
  validationCode?: string | null;
  signature?: string | null;
  signerName?: string | null;
}

export const TreatmentReport: React.FC<ITreatmentReportProps> = ({
  treatment,
  items,
  linkedProducts = [],
  retailItems = [],
  validationCode,
  signature,
  signerName,
}) => {
  const { company } = useCompany();
  const { clinic } = useAuth();
  const today = new Date().toLocaleDateString('fr-CA', {
    timeZone: 'America/Araguaina',
  });

  const date =
    treatment?.created_at != undefined
      ? treatment?.created_at?.toString().split('T')[0]
      : today;

  if (!treatment) return <></>;

  return (
    <RViewer>
      <RPage>
        <View style={S.Styles.container}>
          <RHeader company={company} />
          <View style={S.Styles.containerClient}>
            <View style={S.Styles.containerTreatmentView}>
              <View style={S.Styles.row}>
                <Text style={S.Styles.label}>Nº:</Text>
                <Text style={S.Styles.value}>{treatment?.code}</Text>
              </View>
              <View style={S.Styles.row}>
                <Text style={S.Styles.label}>Cadastrado:</Text>
                <Text style={S.Styles.value}>{formatDate(`${date}`)}</Text>
              </View>
            </View>
          </View>
          <View style={S.Styles.containerClient}>
            <View style={S.Styles.row}>
              <Text style={S.Styles.label}>Nome:</Text>
              <Text style={S.Styles.value}>{treatment?.client_name}</Text>
            </View>
            <View style={S.Styles.row}>
              <Text style={S.Styles.label}>Data Nasc.:</Text>
              <Text style={S.Styles.value}>
                {formatDate(treatment?.client_birthday)}
              </Text>
            </View>
            <View style={S.Styles.row}>
              <Text style={S.Styles.label}>CPF:</Text>
              <Text style={S.Styles.value}>{treatment?.client_cpf_cnpj}</Text>
            </View>
            <View style={S.Styles.row}>
              <Text style={S.Styles.label}>Celular:</Text>
              <Text style={S.Styles.value}>{treatment?.client_cell_phone}</Text>
            </View>
          </View>
          <View style={S.Styles.titleProcedures}>
            <Text>Procedimento(s)</Text>
          </View>
          <View style={S.Styles.tableContainer}>
            <View style={S.Styles.rowHeader}>
              <Text style={S.Styles.columnHeader}>#</Text>
              <Text style={S.Styles.columnHeader}>Procedimento</Text>
              <Text style={S.Styles.columnHeader}>Valor Un.</Text>
              <Text style={S.Styles.columnHeader}>Qtd.</Text>
              <Text style={S.Styles.columnHeader}>Desconto</Text>
              <Text style={S.Styles.columnHeader}>Valor</Text>
              <Text style={S.Styles.columnHeader}>Total</Text>
            </View>
            {items?.map((item, index) => (
              <View key={item.id} style={S.Styles.rowBody}>
                <Text style={S.Styles.columnBody}>{index + 1}</Text>
                <Text style={S.Styles.columnBody}>{item.procedure_name}</Text>
                <Text style={S.Styles.columnBody}>
                  {currency(item.value ?? 0)}
                </Text>
                <Text style={S.Styles.columnBody}>{item.quantity}</Text>
                <Text style={S.Styles.columnBody}>
                  {currency(item.discount)}
                </Text>
                <Text style={S.Styles.columnBody}>
                  {currency(item.value - +item.discount)}
                </Text>
                <Text style={S.Styles.columnBody}>
                  {currency(+item.value * item.quantity)}
                </Text>
              </View>
            ))}
          </View>
          <View style={S.Styles.containerTotalView}>
            <View style={S.Styles.containerRow}>
              <Text style={S.Styles.totalText}>SubTotal:</Text>
              <Text style={S.Styles.totalText}>
                {currency(treatment?.total)}
              </Text>
            </View>
            <View style={S.Styles.containerRow}>
              <Text style={S.Styles.totalText}>Desconto:</Text>
              <Text style={S.Styles.totalText}>
                {currency(treatment.discount)}
              </Text>
            </View>
            <View style={S.Styles.containerRow}>
              <Text style={S.Styles.totalText}>Total:</Text>
              <Text style={S.Styles.totalText}>
                {currency(treatment?.total - treatment?.discount)}
              </Text>
            </View>
          </View>
          {(linkedProducts.length > 0 || retailItems.length > 0) && (
            <>
              <View style={S.Styles.titleProcedures}>
                <Text>Resumo Comercial</Text>
              </View>
              <View style={S.Styles.tableContainer}>
                <View style={S.Styles.rowHeader}>
                  <Text style={S.Styles.columnHeader}>Categoria</Text>
                  <Text style={S.Styles.columnHeader}>Itens</Text>
                  <Text style={S.Styles.columnHeader}>Valor</Text>
                </View>
                <View style={S.Styles.rowBody}>
                  <Text style={S.Styles.columnBody}>Produtos vinculados</Text>
                  <Text style={S.Styles.columnBody}>
                    {linkedProducts.length}
                  </Text>
                  <Text style={S.Styles.columnBody}>
                    {currency(
                      linkedProducts.reduce(
                        (sum, item) => sum + Number(item.total_price || 0),
                        0,
                      ),
                    )}
                  </Text>
                </View>
                <View style={S.Styles.rowBody}>
                  <Text style={S.Styles.columnBody}>Venda avulsa</Text>
                  <Text style={S.Styles.columnBody}>{retailItems.length}</Text>
                  <Text style={S.Styles.columnBody}>
                    {currency(
                      retailItems.reduce(
                        (sum, item) => sum + Number(item.total_price || 0),
                        0,
                      ),
                    )}
                  </Text>
                </View>
              </View>
            </>
          )}
          <RSignature
            professional_name={treatment?.professional_name}
            signer_name={signerName || treatment?.professional_name}
            signature={signature}
            signed_at={treatment?.signed_at}
            separate_page_when_signed
            validation_code={
              validationCode || treatment?.signature_validation_code
            }
            clinic={clinic}
          />
        </View>
      </RPage>
    </RViewer>
  );
};
